import React, { useState } from 'react';
import '../../styles/AdminAddProject.scss';

const AdminAddProject = () => {
  const [formData, setFormData] = useState({
    projectName: '',
    category: '',
    domain: '',
    descriptions: ['', '', '', '', '', ''],
    setupInfo: '',
    images: [],
    demoVideoFile: null,
    installationVideoFile: null,
    reportFile: null,
    zipFile: null,
    price: 0,
    discount_price: '',
    currency: 'IN',
    access_type: 'Free',
    project_type: 'Free',
    whatsapp_number: '9762424851',
  });

  const [imagePreviews, setImagePreviews] = useState([]);
  const [developerImagePreview, setDeveloperImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});

  const categories = [
    'AI & Machine Learning',
    'Web Development',
    'Android App Development',
    'Data Science & Data Analytics',
    'IoT & Embedded Systems',
    'Cloud Computing & DevOps',
    'Cyber Security & Ethical Hacking',
    'Blockchain & Cryptography',
    'Desktop Application Development',
    'Game Development',
    'Other'
  ];
  const domains = ['BCA/BCS/MCA', 'BE/B.Tech/ME/M.Tech/Diploma'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDescriptionChange = (index, value) => {
    const newDescriptions = [...formData.descriptions];
    newDescriptions[index] = value;
    setFormData(prev => ({
      ...prev,
      descriptions: newDescriptions
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (formData.images.length + files.length > 6) {
      setErrors(prev => ({
        ...prev,
        images: 'Maximum 6 images allowed'
      }));
      return;
    }

    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, file]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleDeveloperImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDeveloperImagePreview(reader.result);
        setFormData(prev => ({
          ...prev,
          developerImage: file
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    console.log(`File selected for ${fieldName}:`, file);
    if (file) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
      console.log(`Updated form data with ${fieldName}:`, file.name);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.projectName.trim()) newErrors.projectName = 'Project name is required';
    if (!formData.category) newErrors.category = 'Technology domain is required';
    if (!formData.domain) newErrors.domain = 'Academic domain is required';
    if (!formData.descriptions[0].trim()) newErrors.descriptions = 'At least one description is required';
    return newErrors;
  };

  const uploadFiles = async () => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    let uploadedPaths = {
      demoVideoUrl: null,
      installationVideoUrl: null,
      reportFile: null,
      zipFile: null,
      images: []
    };

    try {
      // Upload demo video
      if (formData.demoVideoFile) {
        console.log('📤 Uploading demo video:', formData.demoVideoFile.name);
        const formDataDemo = new FormData();
        formDataDemo.append('file', formData.demoVideoFile);
        
        const demoRes = await fetch(`${API_BASE}/api/projects/upload/single-file?file_type=video_demo`, {
          method: 'POST',
          body: formDataDemo
          // Don't set Content-Type - browser will set it with boundary
        });
        
        if (!demoRes.ok) {
          const errorText = await demoRes.text();
          console.error('❌ Demo video failed:', demoRes.status, errorText);
          throw new Error(`Demo video: ${errorText || demoRes.statusText}`);
        }
        
        const demoData = await demoRes.json();
        console.log('✅ Demo video uploaded:', demoData.filename);
        uploadedPaths.demoVideoUrl = demoData.file;
      }

      // Upload installation video
      if (formData.installationVideoFile) {
        console.log('📤 Uploading installation video:', formData.installationVideoFile.name);
        const formDataInst = new FormData();
        formDataInst.append('file', formData.installationVideoFile);
        
        const instRes = await fetch(`${API_BASE}/api/projects/upload/single-file?file_type=video_installation`, {
          method: 'POST',
          body: formDataInst
        });
        
        if (!instRes.ok) {
          const errorText = await instRes.text();
          console.error('❌ Installation video failed:', instRes.status, errorText);
          throw new Error(`Installation video: ${errorText || instRes.statusText}`);
        }
        
        const instData = await instRes.json();
        console.log('✅ Installation video uploaded:', instData.filename);
        uploadedPaths.installationVideoUrl = instData.file;
      }

      // Upload report file
      if (formData.reportFile) {
        console.log('📤 Uploading report file:', formData.reportFile.name);
        const formDataReport = new FormData();
        formDataReport.append('file', formData.reportFile);
        
        const reportRes = await fetch(`${API_BASE}/api/projects/upload/single-file?file_type=report`, {
          method: 'POST',
          body: formDataReport
        });
        
        if (!reportRes.ok) {
          const errorText = await reportRes.text();
          console.error('❌ Report file failed:', reportRes.status, errorText);
          throw new Error(`Report file: ${errorText || reportRes.statusText}`);
        }
        
        const reportData = await reportRes.json();
        console.log('✅ Report file uploaded:', reportData.filename);
        uploadedPaths.reportFile = reportData.file;
      }

      // Upload zip file
      if (formData.zipFile) {
        console.log('📤 Uploading ZIP file:', formData.zipFile.name);
        const formDataZip = new FormData();
        formDataZip.append('file', formData.zipFile);
        
        const zipRes = await fetch(`${API_BASE}/api/projects/upload/single-file?file_type=zip`, {
          method: 'POST',
          body: formDataZip
        });
        
        if (!zipRes.ok) {
          const errorText = await zipRes.text();
          console.error('❌ ZIP file failed:', zipRes.status, errorText);
          throw new Error(`ZIP file: ${errorText || zipRes.statusText}`);
        }
        
        const zipData = await zipRes.json();
        console.log('✅ ZIP file uploaded:', zipData.filename);
        uploadedPaths.zipFile = zipData.file;
      }

      // Upload project images
      if (formData.images && formData.images.length > 0) {
        console.log(`📤 Uploading ${formData.images.length} project images...`);
        const imageFormData = new FormData();
        formData.images.forEach(img => {
          imageFormData.append('files', img);
        });
        
        const imgRes = await fetch(`${API_BASE}/api/projects/upload/images`, {
          method: 'POST',
          body: imageFormData
        });
        
        if (!imgRes.ok) {
          const errorText = await imgRes.text();
          console.error('❌ Project images upload failed:', imgRes.status, errorText);
          throw new Error(`Project images: ${errorText || imgRes.statusText}`);
        }
        
        const imgData = await imgRes.json();
        console.log('✅ Project images uploaded:', imgData.files);
        uploadedPaths.images = imgData.files;
      }

      // Upload report file
      console.log('🎉 All files uploaded successfully:', uploadedPaths);
      return uploadedPaths;
    } catch (error) {
      console.error('🔥 File upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setUploadProgress({ status: 'uploading' });
      console.log('Starting upload process...');
      console.log('Form data:', formData);

      // Upload all files first (only if selected)
      const uploadedFiles = await uploadFiles();
      console.log('All files uploaded:', uploadedFiles);

      // Build individual description fields
      const projectPayload = {
        title: formData.projectName,
        category: formData.category,
        domain: formData.domain,
        description_1: formData.descriptions[0] || null,
        description_2: formData.descriptions[1] || null,
        description_3: formData.descriptions[2] || null,
        description_4: formData.descriptions[3] || null,
        description_5: formData.descriptions[4] || null,
        description_6: formData.descriptions[5] || null,
        setup_info: formData.setupInfo || null,
        images: JSON.stringify(uploadedFiles.images || []),
        demo_video_url: uploadedFiles.demoVideoUrl || null,
        installation_video_url: uploadedFiles.installationVideoUrl || null,
        report_file: uploadedFiles.reportFile || null,
        zip_file: uploadedFiles.zipFile || null,
        price: parseFloat(formData.price) || 0,
        discount_price: parseFloat(formData.discount_price) || null,
        currency: formData.currency || 'IN',
        access_type: formData.access_type || 'Free',
        project_type: formData.project_type || 'Free',
        whatsapp_number: formData.whatsapp_number || '9762424851',
      };

      console.log('Project payload:', projectPayload);

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      // Create project
      const createRes = await fetch(`${API_BASE}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectPayload)
      });

      const responseData = await createRes.json();
      console.log('Create response:', createRes.status, responseData);

      if (createRes.ok) {
        setUploadProgress({ status: 'success' });
        alert('✅ Project added successfully!');
        window.history.back();
      } else {
        throw new Error(responseData.detail || `Failed to create project: ${createRes.status}`);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setUploadProgress({ status: 'error', message: error.message });
      setErrors(prev => ({
        ...prev,
        submit: error.message
      }));
    }
  };

  const handleCancel = () => {
    window.history.back();
  };

  return (
    <div className="admin-add-project-page">
      <div className="form-header sticky">
        <h1>➕ Add New Project</h1>
        <p>Create a new project with comprehensive details, media, and developer information</p>
      </div>

      <form onSubmit={handleSubmit} className="project-form">
        {/* SECTION 1: BASIC PROJECT INFO */}
        <div className="form-card">
          <div className="card-header">
            <h2>🧾 Basic Project Information</h2>
            <span className="section-number">Section 1</span>
          </div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group full-width">
                <label>Project Name *</label>
                <input
                  type="text"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleInputChange}
                  placeholder="Enter project name"
                  className={errors.projectName ? 'error' : ''}
                />
                {errors.projectName && <span className="error-text">{errors.projectName}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group half-width">
                <label>Technology Domain *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={errors.category ? 'error' : ''}
                >
                  <option value="">Select technology domain</option>
                  {categories.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {errors.category && <span className="error-text">{errors.category}</span>}
              </div>
              <div className="form-group half-width">
                <label>Academic Domain *</label>
                <select
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  className={errors.domain ? 'error' : ''}
                >
                  <option value="">Select Domain</option>
                  {domains.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                {errors.domain && <span className="error-text">{errors.domain}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: PROJECT DESCRIPTIONS */}
        <div className="form-card">
          <div className="card-header">
            <h2>📝 Project Descriptions</h2>
            <span className="section-number">Section 2</span>
          </div>
          <div className="card-body">
            <div className="descriptions-grid">
              {formData.descriptions.map((desc, index) => (
                <div key={index} className="description-item">
                  <label>Description {index + 1} {index === 0 && '*'}</label>
                  <textarea
                    value={desc}
                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                    placeholder={`Enter description ${index + 1}...`}
                    rows="4"
                    className={errors.descriptions && index === 0 ? 'error' : ''}
                  />
                  {errors.descriptions && index === 0 && <span className="error-text">{errors.descriptions}</span>}
                </div>
              ))}
            </div>
            <div className="info-note">
              💡 <strong>Tip:</strong> Add detailed descriptions of different aspects of your project. At least the first description is required.
            </div>
          </div>
        </div>

        {/* SECTION 3: PROJECT SETUP */}
        <div className="form-card">
          <div className="card-header">
            <h2>⚙️ Project Setup Information</h2>
            <span className="section-number">Section 3</span>
          </div>
          <div className="card-body">
            <div className="form-group full-width">
              <label>Installation Steps, Requirements & Configuration</label>
              <textarea
                value={formData.setupInfo}
                onChange={(e) => setFormData(prev => ({ ...prev, setupInfo: e.target.value }))}
                placeholder="Enter installation steps, requirements, and configuration details..."
                rows="6"
              />
            </div>
          </div>
        </div>

        {/* SECTION 4: PROJECT IMAGES */}
        <div className="form-card">
          <div className="card-header">
            <h2>🖼️ Project Images</h2>
            <span className="section-number">Section 4</span>
          </div>
          <div className="card-body">
            <div className="upload-area">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                id="image-upload"
                disabled={formData.images.length >= 6}
              />
              <label htmlFor="image-upload" className="upload-label">
                <div className="upload-icon">📁</div>
                <p>Drag and drop images here or click to browse</p>
                <span className="upload-note">(Max 6 images, PNG/JPG/GIF)</span>
              </label>
            </div>
            {errors.images && <span className="error-text">{errors.images}</span>}

            {imagePreviews.length > 0 && (
              <div className="image-preview-grid">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={preview} alt={`Project ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveImage(index)}
                      title="Remove image"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="images-counter">
              {formData.images.length}/6 images uploaded
            </div>
          </div>
        </div>

        {/* SECTION 5: VIDEOS */}
        <div className="form-card">
          <div className="card-header">
            <h2>🎥 Project Videos</h2>
            <span className="section-number">Section 5</span>
          </div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group half-width">
                <label>Demo Project Video (MP4, WebM, MOV)</label>
                <input
                  type="file"
                  accept=".mp4,.webm,.mov,.avi,.mkv"
                  onChange={(e) => handleFileUpload(e, 'demoVideoFile')}
                  className="simple-file-input"
                />
                {formData.demoVideoFile && (
                  <p className="file-selected">✓ {formData.demoVideoFile.name}</p>
                )}
                <p className="file-size-note">Max 100MB</p>
              </div>
              <div className="form-group half-width">
                <label>Installation Video (MP4, WebM, MOV)</label>
                <input
                  type="file"
                  accept=".mp4,.webm,.mov,.avi,.mkv"
                  onChange={(e) => handleFileUpload(e, 'installationVideoFile')}
                  className="simple-file-input"
                />
                {formData.installationVideoFile && (
                  <p className="file-selected">✓ {formData.installationVideoFile.name}</p>
                )}
                <p className="file-size-note">Max 100MB</p>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 6: FILE UPLOADS */}
        <div className="form-card">
          <div className="card-header">
            <h2>📂 Project Files (Report & ZIP)</h2>
            <span className="section-number">Section 6</span>
          </div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group half-width">
                <label>Project Report (PDF)</label>
                <input
                  type="file"
                  accept=".pdf,.docx,.doc"
                  onChange={(e) => handleFileUpload(e, 'reportFile')}
                  className="simple-file-input"
                />
                {formData.reportFile && (
                  <p className="file-selected">✓ {formData.reportFile.name}</p>
                )}
                <p className="file-size-note">Max 25MB</p>
              </div>
              <div className="form-group half-width">
                <label>Project ZIP File (Source Code)</label>
                <input
                  type="file"
                  accept=".zip,.rar,.7z"
                  onChange={(e) => handleFileUpload(e, 'zipFile')}
                  className="simple-file-input"
                />
                {formData.zipFile && (
                  <p className="file-selected">✓ {formData.zipFile.name}</p>
                )}
                <p className="file-size-note">Max 100MB</p>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 7: DEVELOPER DETAILS */}
        <div className="form-card">
          <div className="card-header">
            <h2>👨‍💻 Developer Details</h2>
            <span className="section-number">Section 7</span>
          </div>
          <div className="card-body">
            <div className="developer-layout">
              <div className="developer-image-section">
                <label>Developer Image</label>
                <div className="image-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleDeveloperImageUpload}
                    id="developer-image-upload"
                  />
                  <label htmlFor="developer-image-upload" className="image-upload-label">
                    {developerImagePreview ? (
                      <img src={developerImagePreview} alt="Developer" />
                    ) : (
                      <div className="placeholder">
                        <span>👤</span>
                        <p>Upload Image</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>


            </div>
          </div>
        </div>

        {/* SECTION 8: CONTACT & ACCESS */}
        <div className="form-card">
          <div className="card-header">
            <h2>📞 Contact & Access Details</h2>
            <span className="section-number">Section 8</span>
          </div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group half-width">
                <label>WhatsApp Number</label>
                <div className="tel-input-wrapper">
                  <span className="tel-prefix">+91</span>
                  <input
                    type="tel"
                    name="whatsapp_number"
                    value={formData.whatsapp_number || ''}
                    onChange={handleInputChange}
                    placeholder="9762424851"
                    className="form-input tel-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 9: ACTION BUTTONS */}
        <div className="form-card">
          <div className="card-body">
            {errors.submit && (
              <div className="submit-error">
                ⚠️ Error: {errors.submit}
              </div>
            )}
            <div className="action-buttons">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancel}
                disabled={uploadProgress.status === 'uploading'}
              >
                ✕ Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={uploadProgress.status === 'uploading'}
              >
                {uploadProgress.status === 'uploading' ? '⏳ Uploading...' : '✓ Add Project'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminAddProject;
